'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { KeyboardEvent, useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ACTIVE_ORDERS_QK } from '@/lib/api/history/hooks';
import type { ActiveOrder } from '@/lib/api/history/models';
import { useCancelLimitOrder, useUpdateLimitOrder } from '@/lib/api/order/hooks';
import { ExpirationEdit } from '@/lib/components/ExpirationEdit';
import {
  useTradingActiveOrders,
  useTradingSharesBalance,
} from '@/lib/components/Trading/TradingDataContext';
import { useCoins } from '@/lib/providers/CoinsProvider';
import { useOrderFormStore, useSelectedOutcome } from '@/lib/store/order-form-store';
import { Icon } from '@/lib/ui-kit/icons';
import { InlineEmpty } from '@/lib/ui-kit/inline-empty';
import { Skeleton } from '@/lib/ui-kit/skeleton';
import { Spinner } from '@/lib/ui-kit/spinner';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { SHARE_DECIMALS } from '@/lib/utils/calculateFees';
import { formatCentsValue, formatMoney } from '@/lib/utils/formatAmount';
import { formatQuantity } from '@/lib/utils/formatNumber';

type OpenOrderRow = {
  id: string;
  candidate: string;
  outcome: 'Yes' | 'No';
  side: 'Buy' | 'Sell';
  price: string;
  filled: string;
  request: string;
  orderValue: string;
  expiration: string;
  originalOrder: ActiveOrder;
};

type EventOpenOrdersTableProps = {
  outcomeId: string;
  isTabActive?: boolean;
};

function mapOrderToRow(
  order: ActiveOrder,
  outcomeName: string,
  resolutions?: Array<{ id: string; name: string; isPositive: boolean }>,
  endOfDayLabel?: string,
): OpenOrderRow {
  let outcome: 'Yes' | 'No' = 'Yes';
  if (resolutions && order.assetId) {
    const resolution = resolutions.find((r) => r.id === order.assetId);
    if (resolution) {
      outcome = resolution.isPositive ? 'Yes' : 'No';
    }
  } else {
    outcome = order.price > 0.5 ? 'Yes' : 'No';
  }

  const priceInCents = Number(formatCentsValue(order.price * 100));

  // Plain, ungrouped: the request string is edited inline and parsed back.
  const filled = formatMoney(order.quantity, { decimals: SHARE_DECIMALS, plain: true });
  const request = formatMoney(order.originalQuantity, { decimals: SHARE_DECIMALS, plain: true });

  const requestMinusFilled = order.originalQuantity - order.quantity;
  const orderValue = (priceInCents * requestMinusFilled) / 100;

  let expiration = '-';
  if (order.expireAt) {
    try {
      const expireDate = new Date(order.expireAt);
      const hours = expireDate.getUTCHours();
      const minutes = expireDate.getUTCMinutes();
      const seconds = expireDate.getUTCSeconds();

      const today = new Date();
      const isToday =
        expireDate.getUTCDate() === today.getUTCDate() &&
        expireDate.getUTCMonth() === today.getUTCMonth() &&
        expireDate.getUTCFullYear() === today.getUTCFullYear();

      if (hours === 23 && minutes === 59 && (seconds === 59 || seconds === 0) && isToday) {
        expiration = endOfDayLabel || 'End of the day';
      } else {
        const day = expireDate.getUTCDate();
        const monthNames = [
          'JAN',
          'FEB',
          'MAR',
          'APR',
          'MAY',
          'JUN',
          'JUL',
          'AUG',
          'SEP',
          'OCT',
          'NOV',
          'DEC',
        ];
        const month = monthNames[expireDate.getUTCMonth()];
        const year = expireDate.getUTCFullYear();
        const hoursStr = String(hours).padStart(2, '0');
        const minutesStr = String(minutes).padStart(2, '0');
        expiration = `${day}-${month}-${year}, ${hoursStr}:${minutesStr} UTC`;
      }
    } catch {
      expiration = '-';
    }
  }

  return {
    id: order.id,
    candidate: outcomeName,
    outcome,
    side: order.side === 'BUY' ? 'Buy' : 'Sell',
    price: `${priceInCents}¢`,
    filled,
    request,
    orderValue: formatMoney(orderValue, { prefix: '$' }),
    expiration,
    originalOrder: order,
  };
}

const headerCellClass = 'text-[10px] leading-none text-v2-text-secondary';
const bodyCellClass = 'text-[13px] leading-[1.5] text-v2-text-primary';

const IconButton = ({
  onClick,
  ariaLabel,
  children,
  disabled,
}: {
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-[8px] text-v2-text-secondary transition-colors
      hover:bg-v2-btn-secondary hover:text-v2-text-primary disabled:pointer-events-none disabled:opacity-50"
  >
    {children}
  </button>
);

export const EventOpenOrdersTable = ({
  outcomeId,
  isTabActive: _isTabActive = true,
}: EventOpenOrdersTableProps) => {
  const t = useTranslations('event.openOrders');
  const tCommon = useTranslations('common');
  const tExpiration = useTranslations('expiration');
  const tToasts = useTranslations('toasts.orders');
  const activeOrdersData = useTradingActiveOrders(outcomeId);
  const queryClient = useQueryClient();
  const cancelLimitOrderMutation = useCancelLimitOrder();
  const updateLimitOrderMutation = useUpdateLimitOrder();
  const event = useOrderFormStore.use.event();
  const { coins } = useCoins();

  const usdcCoin = coins.find((coin) => coin.shortName.toUpperCase() === 'USDC');
  const quoteAssetId = usdcCoin?.id || '';

  const queryState = queryClient.getQueryState([ACTIVE_ORDERS_QK, outcomeId]);
  const isLoadingActiveOrders = queryState?.status === 'pending' && activeOrdersData === undefined;

  const isLoading = isLoadingActiveOrders;

  const outcome = useSelectedOutcome();
  const outcomeName = outcome?.question || 'Outcome';
  const resolutions = outcome?.r;

  const yesSharesBalance = useTradingSharesBalance(outcomeId, 'yes');
  const noSharesBalance = useTradingSharesBalance(outcomeId, 'no');

  const [editingPriceRowId, setEditingPriceRowId] = useState<string | null>(null);
  const [tempPrice, setTempPrice] = useState<string>('');

  const [editingRequestRowId, setEditingRequestRowId] = useState<string | null>(null);
  const [tempRequest, setTempRequest] = useState<string>('');

  const activeOrders = useMemo(() => {
    return activeOrdersData?.items ?? [];
  }, [activeOrdersData]);

  const endOfDayLabel = tExpiration('endOfDay');
  const data = useMemo(() => {
    return activeOrders.map((order) =>
      mapOrderToRow(order, outcomeName, resolutions, endOfDayLabel),
    );
  }, [activeOrders, outcomeName, resolutions, endOfDayLabel]);

  const startPriceEdit = useCallback((rowId: string, currentPrice: string) => {
    setEditingPriceRowId(rowId);
    const numeric = currentPrice.replace(/[^0-9.]/g, '');
    setTempPrice(numeric);
  }, []);

  const cancelPriceEdit = useCallback(() => {
    setEditingPriceRowId(null);
    setTempPrice('');
  }, []);

  const savePriceEdit = useCallback(async () => {
    if (!editingPriceRowId) return;

    const row = data.find((r) => r.id === editingPriceRowId);
    if (!row || !event) {
      cancelPriceEdit();
      return;
    }

    const newPriceValue = parseFloat(tempPrice);
    if (isNaN(newPriceValue)) {
      toast.error(tToasts('invalidPrice'));
      return;
    }

    if (newPriceValue < 0 || newPriceValue > 99) {
      toast.error(tToasts('priceRange'));
      return;
    }

    const currentPriceInCents = Math.round(row.originalOrder.price * 100);

    if (newPriceValue === currentPriceInCents) {
      cancelPriceEdit();
      return;
    }

    try {
      const resolution = resolutions?.find((r) => r.id === row.originalOrder.assetId);
      if (!resolution) {
        toast.error(tToasts('resolutionNotFound'));
        cancelPriceEdit();
        return;
      }

      if (!quoteAssetId) {
        toast.error(tToasts('usdcNotFound'));
        cancelPriceEdit();
        return;
      }

      const request = row.originalOrder.originalQuantity;
      const filled = row.originalOrder.quantity;
      const quantityDifference = request - filled;

      await updateLimitOrderMutation.mutateAsync({
        orderId: editingPriceRowId,
        body: {
          eventId: event.id,
          eventOutcomeId: outcomeId,
          baseAssetId: resolution.id,
          quoteAssetId,
          side: row.originalOrder.side,
          price: newPriceValue / 100,
          quantity: quantityDifference,
          expireAt: row.originalOrder.expireAt,
        },
      });

      toast.success(tToasts('priceUpdated'));
      cancelPriceEdit();
    } catch {
      // Error handled by global MutationCache onError
    }
  }, [
    editingPriceRowId,
    tempPrice,
    data,
    event,
    resolutions,
    outcomeId,
    updateLimitOrderMutation,
    cancelPriceEdit,
    quoteAssetId,
    tToasts,
  ]);

  const onPriceKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        savePriceEdit();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelPriceEdit();
      }
    },
    [savePriceEdit, cancelPriceEdit],
  );

  const startRequestEdit = useCallback((rowId: string, currentRequest: string) => {
    setEditingRequestRowId(rowId);
    setTempRequest(currentRequest);
  }, []);

  const cancelRequestEdit = useCallback(() => {
    setEditingRequestRowId(null);
    setTempRequest('');
  }, []);

  const saveRequestEdit = useCallback(async () => {
    if (!editingRequestRowId) return;

    const row = data.find((r) => r.id === editingRequestRowId);
    if (!row || !event) {
      cancelRequestEdit();
      return;
    }

    const newQuantity = parseFloat(tempRequest);

    if (isNaN(newQuantity) || newQuantity <= 0) {
      toast.error(tToasts('invalidQuantity'));
      return;
    }

    const currentQuantity = row.originalOrder.originalQuantity;

    if (newQuantity === currentQuantity) {
      cancelRequestEdit();
      return;
    }

    if (row.originalOrder.side === 'SELL') {
      const sharesBalance = row.outcome === 'Yes' ? yesSharesBalance : noSharesBalance;
      const currentUnfilled = row.originalOrder.originalQuantity - row.originalOrder.quantity;
      const availableShares = (sharesBalance ?? 0) + currentUnfilled;

      if (sharesBalance !== undefined && newQuantity > availableShares) {
        toast.error(tToasts('quantityExceedsBalance', { shares: availableShares }));
        return;
      }
    }

    try {
      const resolution = resolutions?.find((r) => r.id === row.originalOrder.assetId);
      if (!resolution) {
        toast.error(tToasts('resolutionNotFound'));
        cancelRequestEdit();
        return;
      }

      if (!quoteAssetId) {
        toast.error(tToasts('usdcNotFound'));
        cancelRequestEdit();
        return;
      }

      await updateLimitOrderMutation.mutateAsync({
        orderId: editingRequestRowId,
        body: {
          eventId: event.id,
          eventOutcomeId: outcomeId,
          baseAssetId: resolution.id,
          quoteAssetId,
          side: row.originalOrder.side,
          price: row.originalOrder.price,
          quantity: newQuantity,
          expireAt: row.originalOrder.expireAt,
        },
      });

      toast.success(tToasts('quantityUpdated'));
      cancelRequestEdit();
    } catch {
      // Error handled by global MutationCache onError
    }
  }, [
    editingRequestRowId,
    tempRequest,
    data,
    event,
    resolutions,
    outcomeId,
    updateLimitOrderMutation,
    cancelRequestEdit,
    yesSharesBalance,
    noSharesBalance,
    quoteAssetId,
    tToasts,
  ]);

  const onRequestKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        saveRequestEdit();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelRequestEdit();
      }
    },
    [saveRequestEdit, cancelRequestEdit],
  );

  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      try {
        await cancelLimitOrderMutation.mutateAsync(orderId);
        toast.success(tToasts('cancelled'));
      } catch {
        // Error handled by global MutationCache onError
      }
    },
    [cancelLimitOrderMutation, tToasts],
  );

  const handleSaveExpiration = useCallback(
    async (orderId: string, isoDate: string) => {
      const row = data.find((r) => r.id === orderId);
      if (!row || !event) {
        toast.error(tToasts('notFound'));
        throw new Error('Order not found');
      }

      try {
        const resolution = resolutions?.find((r) => r.id === row.originalOrder.assetId);
        if (!resolution) {
          toast.error(tToasts('resolutionNotFound'));
          throw new Error('Failed to find resolution');
        }

        if (!quoteAssetId) {
          toast.error(tToasts('usdcNotFound'));
          throw new Error('USDC asset ID not found');
        }

        const request = row.originalOrder.originalQuantity;
        const filled = row.originalOrder.quantity;
        const quantityDifference = request - filled;

        await updateLimitOrderMutation.mutateAsync({
          orderId,
          body: {
            eventId: event.id,
            eventOutcomeId: outcomeId,
            baseAssetId: resolution.id,
            quoteAssetId,
            side: row.originalOrder.side,
            price: row.originalOrder.price,
            quantity: quantityDifference,
            expireAt: isoDate,
          },
        });

        toast.success(tToasts('expirationUpdated'));
      } catch (error) {
        throw error;
      }
    },
    [data, event, resolutions, quoteAssetId, outcomeId, updateLimitOrderMutation, tToasts],
  );

  const handleDeleteExpiration = useCallback(
    async (orderId: string) => {
      const row = data.find((r) => r.id === orderId);
      if (!row || !event) {
        toast.error(tToasts('notFound'));
        throw new Error('Order not found');
      }

      try {
        const resolution = resolutions?.find((r) => r.id === row.originalOrder.assetId);
        if (!resolution) {
          toast.error(tToasts('resolutionNotFound'));
          throw new Error('Failed to find resolution');
        }

        if (!quoteAssetId) {
          toast.error(tToasts('usdcNotFound'));
          throw new Error('USDC asset ID not found');
        }

        const request = row.originalOrder.originalQuantity;
        const filled = row.originalOrder.quantity;
        const quantityDifference = request - filled;

        await updateLimitOrderMutation.mutateAsync({
          orderId,
          body: {
            eventId: event.id,
            eventOutcomeId: outcomeId,
            baseAssetId: resolution.id,
            quoteAssetId,
            side: row.originalOrder.side,
            price: row.originalOrder.price,
            quantity: quantityDifference,
            expireAt: null,
          },
        });

        toast.success(tToasts('expirationDeleted'));
      } catch (error) {
        throw error;
      }
    },
    [data, event, resolutions, quoteAssetId, outcomeId, updateLimitOrderMutation, tToasts],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full !bg-v2-btn-secondary-hover" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <InlineEmpty>{t('noOpenOrders')}</InlineEmpty>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 overflow-x-auto px-3">
      <div className="flex min-h-6 w-full min-w-[720px] items-center gap-3 border-b border-v2-border pb-2">
        <span className={cn('flex-1', headerCellClass)}>{t('outcome')}</span>
        <span className={cn('w-[100px]', headerCellClass)}>{t('side')}</span>
        <span className={cn('flex-1', headerCellClass)}>{t('price')}</span>
        <span className={cn('flex-1', headerCellClass)}>{t('filledRequest')}</span>
        <span className={cn('flex-1', headerCellClass)}>{t('orderValue')}</span>
        <span className={cn('w-[160px]', headerCellClass)}>{t('expiration')}</span>
        <span className="w-[75px] shrink-0" aria-hidden />
      </div>

      <div className="scrollbar-hide flex max-h-[300px] flex-col gap-4 overflow-y-auto">
        {data.map((row) => {
          const isEditingPrice = editingPriceRowId === row.id;
          const isEditingRequest = editingRequestRowId === row.id;
          const isMutating =
            updateLimitOrderMutation.isPending &&
            (editingPriceRowId === row.id || editingRequestRowId === row.id);

          return (
            <div key={row.id} className="flex w-full min-w-[720px] items-center gap-3">
              <div className="flex flex-1 items-center">
                <span
                  className={cn(
                    'flex w-10 items-center justify-center rounded-[8px] px-2 py-0.5 text-[12px] leading-4 tracking-[-0.12px]',
                    row.outcome === 'No' ? 'text-red-400' : 'text-v2-green',
                  )}
                  style={{
                    backgroundColor:
                      row.outcome === 'No' ? 'rgba(251, 78, 75, 0.1)' : 'rgba(23, 210, 113, 0.1)',
                  }}
                >
                  {row.outcome === 'Yes' ? tCommon('yes') : tCommon('no')}
                </span>
              </div>

              <span className={cn('w-[100px]', bodyCellClass)}>
                {row.side === 'Buy' ? tCommon('buy') : tCommon('sell')}
              </span>

              <div className="flex flex-1 items-center gap-1">
                {isEditingPrice ? (
                  <>
                    <IconButton
                      onClick={savePriceEdit}
                      ariaLabel="Confirm price"
                      disabled={isMutating}
                    >
                      {isMutating ? <Spinner size={16} /> : <Icon name="Check" size={16} />}
                    </IconButton>
                    <input
                      value={tempPrice}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                        const num = parseFloat(cleaned);
                        if (!isNaN(num) && num > 99) {
                          setTempPrice('99');
                        } else {
                          setTempPrice(cleaned);
                        }
                      }}
                      onKeyDown={onPriceKeyDown}
                      autoFocus
                      inputMode="decimal"
                      className="h-8 w-[72px] rounded-[10px] bg-v2-btn-secondary px-3 py-2 text-[13px] leading-[1.5] text-v2-text-primary
                        outline-none"
                    />
                    <IconButton onClick={cancelPriceEdit} ariaLabel="Cancel edit">
                      <Icon name="Close" size={20} />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <span className={bodyCellClass}>{row.price}</span>
                    <IconButton
                      onClick={() => startPriceEdit(row.id, row.price)}
                      ariaLabel="Edit price"
                    >
                      <Icon name="Edit" size={16} />
                    </IconButton>
                  </>
                )}
              </div>

              <div className="flex flex-1 items-center gap-1">
                {isEditingRequest ? (
                  <>
                    <IconButton
                      onClick={saveRequestEdit}
                      ariaLabel="Confirm quantity"
                      disabled={isMutating}
                    >
                      {isMutating ? <Spinner size={16} /> : <Icon name="Check" size={16} />}
                    </IconButton>
                    <input
                      value={tempRequest}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d{0,2}$/.test(value)) {
                          const sanitized = value.replace(/^0+(\d)/, '$1');
                          setTempRequest(sanitized);
                        }
                      }}
                      onKeyDown={onRequestKeyDown}
                      autoFocus
                      inputMode="numeric"
                      className="h-8 w-[80px] rounded-[10px] bg-v2-btn-secondary px-3 py-2 text-[13px] leading-[1.5] text-v2-text-primary
                        outline-none"
                    />
                    <IconButton onClick={cancelRequestEdit} ariaLabel="Cancel edit">
                      <Icon name="Close" size={20} />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <span className={bodyCellClass}>
                      {formatQuantity(row.originalOrder.quantity)}/
                      {formatQuantity(parseFloat(row.request))}
                    </span>
                    <IconButton
                      onClick={() => startRequestEdit(row.id, row.request)}
                      ariaLabel="Edit quantity"
                    >
                      <Icon name="Edit" size={16} />
                    </IconButton>
                  </>
                )}
              </div>

              <span className={cn('flex-1', bodyCellClass)}>{row.orderValue}</span>

              <div className="w-[160px]">
                <ExpirationEdit
                  value={row.expiration}
                  onSave={(isoDate) => handleSaveExpiration(row.id, isoDate)}
                  onDelete={() => handleDeleteExpiration(row.id)}
                />
              </div>

              <Button
                appearance="neutral"
                size="M"
                onClick={() => handleCancelOrder(row.originalOrder.id)}
                className="h-10 w-[75px] shrink-0 px-4"
              >
                {t('cancel')}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
